import React, { Fragment, useCallback, useContext } from "react";
import {
  any,
  set,
  path,
  lensPath,
  equals,
  lensProp,
  view,
  remove,
  includes,
} from "ramda";
import { notBlank, notEmpty } from "../../util/ext";
import { useCategories } from "../vocabularyServices";
import { MediaContext } from "./media-context";
import { matchSorter } from "match-sorter";

import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import AudienceSelect from "./AudienceSelect";
import ColumnHeader from "../content/edit/ColumnHeader";
import VirtualizedListbox from "../../components/VirtualizedListbox";

const EditCategoriesColumn = props => {
  const context = useContext(MediaContext);
  const categories = useCategories();
  const { className, showAudience, showFourCategories, validator } = props;

  const getOptionDisabled = option =>
    any(category => option.id === category.id, context.media.bisacCategories);

  const handleCategoryChange = useCallback(
    path => (event, value) => {
      if (notBlank(value)) {
        context.setMedia(prev => set(lensPath(path), value, prev));
      } else {
        const [list, index] = path;
        context.setMedia(prev =>
          set(
            lensProp(list),
            remove(index, 1, view(lensProp(list), prev)),
            prev,
          ),
        );
      }
    },
    [context],
  );

  const handleTextChange = useCallback(
    path => event => {
      const value = event.target.value;
      context.setMedia(prev => set(lensPath(path), value, prev));
    },
    [context],
  );

  const filterOptions = (options, { inputValue }) =>
    matchSorter(options, inputValue, {
      keys: ["headingDescription", "bisacCode"],
    });

  return (
    <div className={className}>
      <Grid item xs={12}>
        <ColumnHeader header="Categories" paddingBottom={0} />
      </Grid>
      {notEmpty(context.media) && (
        <Fragment>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disableClearable={includes(context.media.type, [
                  "BOOK",
                  "AUDIOBOOK",
                ])}
                options={categories.data}
                value={path(["bisacCategories", 0], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                filterOptions={filterOptions}
                renderOption={({ key, ...props }, cat) => (
                  <li key={key} {...props}>
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  </li>
                )}
                ListboxComponent={VirtualizedListbox}
                ListboxProps={{ itemSize: 80 }}
                getOptionDisabled={getOptionDisabled}
                onChange={handleCategoryChange(["bisacCategories", 0])}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      required={
                        includes(context.media.type, ["BOOK", "AUDIOBOOK"]) ||
                        includes(context.media.mediaType, ["anthology"])
                      }
                      type="text"
                      onBlur={() =>
                        context.setErrors(
                          validator.validateField(
                            "bisacCategories",
                            context.media.bisacCategories[0],
                          ),
                        )
                      }
                      error={validator.hasError(
                        context.errors,
                        "bisacCategories",
                      )}
                      helperText={validator.firstError(
                        context.errors,
                        "bisacCategories",
                      )}
                      name={"media-category1-" + Math.random()}
                      variant="standard"
                      label="Category 1"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                options={categories.data}
                disabled={context.media.bisacCategories.length < 1}
                value={path(["bisacCategories", 1], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                onChange={handleCategoryChange(["bisacCategories", 1])}
                filterOptions={filterOptions}
                ListboxComponent={VirtualizedListbox}
                ListboxProps={{ itemSize: 80 }}
                renderOption={(props, cat) => (
                  <li {...props}>
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  </li>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category2-" + Math.random()}
                      variant="standard"
                      label="Category 2"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disabled={context.media.bisacCategories.length < 2}
                autoComplete
                options={categories.data}
                value={path(["bisacCategories", 2], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                onChange={handleCategoryChange(["bisacCategories", 2])}
                filterOptions={filterOptions}
                ListboxComponent={VirtualizedListbox}
                ListboxProps={{ itemSize: 80 }}
                renderOption={(props, cat) => (
                  <li {...props}>
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  </li>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category3-" + Math.random()}
                      variant="standard"
                      label="Category 3"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          {showFourCategories && (
            <Grid item xs={12}>
              {categories.isLoading ? (
                <Skeleton variant="text" />
              ) : (
                <Autocomplete
                  disabled={context.media.bisacCategories.length < 3}
                  autoComplete
                  options={categories.data}
                  value={path(["bisacCategories", 3], context.media) || ""}
                  getOptionLabel={cat =>
                    notBlank(cat) ? cat.headingDescription : ""
                  }
                  isOptionEqualToValue={(opt, val) =>
                    notBlank(val) && equals(opt.id, val.id)
                  }
                  onChange={handleCategoryChange(["bisacCategories", 3])}
                  filterOptions={filterOptions}
                  ListboxComponent={VirtualizedListbox}
                  ListboxProps={{ itemSize: 80 }}
                  renderOption={(props, cat) => (
                    <li {...props}>
                      <Tooltip
                        title={cat.headingDescription}
                        placement="bottom-start"
                      >
                        <ListItemText
                          primaryTypographyProps={{ noWrap: true }}
                          primary={cat.headingDescription}
                          secondary={cat.bisacCode}
                        />
                      </Tooltip>
                    </li>
                  )}
                  renderInput={params => {
                    params.inputProps["aria-autocomplete"] = "none";
                    params.inputProps["id"] = null;
                    return (
                      <TextField
                        {...params}
                        type="text"
                        name={"media-category4-" + Math.random()}
                        variant="standard"
                        label="Category 4"
                        fullWidth
                      />
                    );
                  }}
                />
              )}
            </Grid>
          )}
          {showAudience && (
            <Grid item xs={12}>
              <AudienceSelect
                value={path(["audienceCode"], context.media)}
                onChange={handleTextChange(["audienceCode"])}
              />
            </Grid>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default EditCategoriesColumn;
